import { useEffect, useState } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatXOF, formatDate } from "@/lib/format";
import { Plus, Receipt, ChartBar, BookOpen, DownloadSimple } from "@phosphor-icons/react";

export default function Accounting() {
  const [ledger, setLedger] = useState([]);
  const [balance, setBalance] = useState(null);
  const [entries, setEntries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [openExpense, setOpenExpense] = useState(false);
  const [expForm, setExpForm] = useState({ date: new Date().toISOString().slice(0, 10), supplier_name: "", category: "fournitures", description: "", amount: 0, method: "cash" });
  const [journalFilter, setJournalFilter] = useState("all");

  const load = async () => {
    try {
      const [l, b, e, ex, a] = await Promise.all([
        api.get("/reports/ledger"), api.get("/reports/balance-sheet"),
        api.get("/journal-entries"), api.get("/expenses"), api.get("/accounts"),
      ]);
      setLedger(l.data); setBalance(b.data); setEntries(e.data); setExpenses(ex.data); setAccounts(a.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveExpense = async () => {
    try {
      await api.post("/expenses", { ...expForm, amount: Number(expForm.amount) });
      toast.success("Dépense enregistrée + écriture comptable créée");
      setOpenExpense(false);
      setExpForm({ date: new Date().toISOString().slice(0, 10), supplier_name: "", category: "fournitures", description: "", amount: 0, method: "cash" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = journalFilter === "all" ? entries : entries.filter(e => e.journal === journalFilter);
  const journalLabels = { CA: "Caisse", BQ: "Banque", VE: "Ventes", AC: "Achats", PA: "Paie", OD: "Opérations diverses" };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">SYSCOHADA</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Comptabilité</h1>
          <p className="text-muted-foreground text-sm mt-1">Partie double · plan comptable OHADA inspiré</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline" className="rounded-full"
            onClick={() => downloadFile("/export/ledger.xlsx", "grand_livre.xlsx").catch(e => toast.error(e.message))}
            data-testid="btn-export-ledger"
          >
            <DownloadSimple size={14} className="mr-1" /> Excel · Grand livre
          </Button>
          <Button
            variant="outline" className="rounded-full"
            onClick={() => downloadFile("/export/invoices.xlsx", "factures.xlsx").catch(e => toast.error(e.message))}
            data-testid="btn-export-invoices"
          >
            <DownloadSimple size={14} className="mr-1" /> Factures
          </Button>
          <Button
            variant="outline" className="rounded-full"
            onClick={() => downloadFile("/export/payments.xlsx", "paiements.xlsx").catch(e => toast.error(e.message))}
            data-testid="btn-export-payments"
          >
            <DownloadSimple size={14} className="mr-1" /> Paiements
          </Button>
          <Dialog open={openExpense} onOpenChange={setOpenExpense}>
          <DialogTrigger asChild><Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-expense"><Plus size={14} className="mr-1" /> Dépense</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle dépense</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div><Label>Date</Label><Input type="date" value={expForm.date} onChange={e => setExpForm({ ...expForm, date: e.target.value })} data-testid="input-exp-date" /></div>
              <div><Label>Fournisseur / Bénéficiaire</Label><Input value={expForm.supplier_name} onChange={e => setExpForm({ ...expForm, supplier_name: e.target.value })} data-testid="input-exp-supplier" /></div>
              <div>
                <Label>Catégorie</Label>
                <Select value={expForm.category} onValueChange={v => setExpForm({ ...expForm, category: v })}>
                  <SelectTrigger data-testid="select-exp-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fournitures">Fournitures</SelectItem>
                    <SelectItem value="electricite">Électricité / Eau</SelectItem>
                    <SelectItem value="loyer">Loyer</SelectItem>
                    <SelectItem value="telecom">Télécom</SelectItem>
                    <SelectItem value="salaire">Salaire</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea value={expForm.description} onChange={e => setExpForm({ ...expForm, description: e.target.value })} rows={2} /></div>
              <div><Label>Montant</Label><Input type="number" value={expForm.amount} onChange={e => setExpForm({ ...expForm, amount: e.target.value })} data-testid="input-exp-amount" /></div>
              <div>
                <Label>Méthode de paiement</Label>
                <Select value={expForm.method} onValueChange={v => setExpForm({ ...expForm, method: v })}>
                  <SelectTrigger data-testid="select-exp-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Espèces</SelectItem>
                    <SelectItem value="bank">Banque</SelectItem>
                    <SelectItem value="orange_money">Orange Money</SelectItem>
                    <SelectItem value="moov_money">Moov Money</SelectItem>
                    <SelectItem value="wave">Wave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={saveExpense} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-expense">Enregistrer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Total actif</div><div className="font-heading text-2xl font-bold tabular-nums mt-1">{formatXOF(balance.total_assets)}</div></Card>
          <Card className="p-5 border-secondary"><div className="text-xs uppercase tracking-widest text-secondary font-semibold">Produits</div><div className="font-heading text-2xl font-bold tabular-nums mt-1">{formatXOF(balance.total_revenue)}</div></Card>
          <Card className="p-5 border-accent"><div className="text-xs uppercase tracking-widest text-accent font-semibold">Charges</div><div className="font-heading text-2xl font-bold tabular-nums mt-1">{formatXOF(balance.total_expenses)}</div></Card>
          <Card className={`p-5 ${balance.net_result >= 0 ? "border-emerald-500" : "border-destructive"}`}>
            <div className={`text-xs uppercase tracking-widest font-semibold ${balance.net_result >= 0 ? "text-emerald-600" : "text-destructive"}`}>Résultat net</div>
            <div className="font-heading text-2xl font-bold tabular-nums mt-1">{formatXOF(balance.net_result)}</div>
          </Card>
        </div>
      )}

      <Tabs defaultValue="ledger">
        <TabsList>
          <TabsTrigger value="ledger" data-testid="tab-ledger"><BookOpen size={14} className="mr-1" /> Grand livre</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal"><Receipt size={14} className="mr-1" /> Journal</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Dépenses ({expenses.length})</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts">Plan comptable</TabsTrigger>
        </TabsList>

        <TabsContent value="ledger" className="mt-4">
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Code</th><th className="px-4 py-3">Compte</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-right">Débit</th><th className="px-4 py-3 text-right">Crédit</th><th className="px-4 py-3 text-right">Solde</th>
              </tr></thead>
              <tbody>
                {ledger.filter(r => r.debit || r.credit).map((r, i) => (
                  <tr key={r.code} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.code}</td>
                    <td className="px-4 py-3 font-medium">{r.name}</td>
                    <td className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{r.type}</td>
                    <td className="px-4 py-3 tabular-nums text-right">{formatXOF(r.debit)}</td>
                    <td className="px-4 py-3 tabular-nums text-right">{formatXOF(r.credit)}</td>
                    <td className="px-4 py-3 tabular-nums text-right font-semibold">{formatXOF(r.balance)}</td>
                  </tr>
                ))}
                {!ledger.filter(r => r.debit || r.credit).length && <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucune écriture. Émettez une facture ou enregistrez un paiement.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="journal" className="mt-4">
          <div className="flex justify-end mb-3">
            <Select value={journalFilter} onValueChange={setJournalFilter}>
              <SelectTrigger className="w-[220px]" data-testid="select-journal-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les journaux</SelectItem>
                {Object.entries(journalLabels).map(([k, v]) => <SelectItem key={k} value={k}>{k} — {v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            {filtered.map(je => (
              <div key={je.id} className="bg-card border border-border rounded-lg p-4" data-testid={`je-${je.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest font-semibold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">{je.journal}</span>
                    <span className="text-xs text-muted-foreground ml-2 font-mono">{je.reference}</span>
                    <div className="font-medium mt-1">{je.description}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">{formatDate(je.date)}</div>
                </div>
                <table className="w-full text-xs mt-2">
                  <tbody>
                    {je.lines.map((l, i) => (
                      <tr key={i} className="border-t border-border/50">
                        <td className="py-1.5 font-mono w-16">{l.account_code}</td>
                        <td className="py-1.5">{l.label}</td>
                        <td className="py-1.5 tabular-nums text-right w-32">{l.debit ? formatXOF(l.debit) : ""}</td>
                        <td className="py-1.5 tabular-nums text-right w-32">{l.credit ? formatXOF(l.credit) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            {!filtered.length && <div className="text-center text-sm text-muted-foreground py-12">Aucune écriture pour ce journal.</div>}
          </div>
        </TabsContent>

        <TabsContent value="expenses" className="mt-4">
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Fournisseur</th><th className="px-4 py-3">Catégorie</th><th className="px-4 py-3">Description</th><th className="px-4 py-3 text-right">Montant</th>
              </tr></thead>
              <tbody>
                {expenses.map((e, i) => (
                  <tr key={e.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`}>
                    <td className="px-4 py-3">{formatDate(e.date)}</td>
                    <td className="px-4 py-3 font-medium">{e.supplier_name}</td>
                    <td className="px-4 py-3 text-xs uppercase tracking-wider">{e.category}</td>
                    <td className="px-4 py-3 text-muted-foreground">{e.description}</td>
                    <td className="px-4 py-3 tabular-nums text-right font-semibold">{formatXOF(e.amount)}</td>
                  </tr>
                ))}
                {!expenses.length && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucune dépense.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="accounts" className="mt-4">
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Code</th><th className="px-4 py-3">Nom</th><th className="px-4 py-3">Type</th>
              </tr></thead>
              <tbody>
                {accounts.map((a, i) => (
                  <tr key={a.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`}>
                    <td className="px-4 py-3 font-mono text-xs">{a.code}</td>
                    <td className="px-4 py-3 font-medium">{a.name}</td>
                    <td className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{a.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
